<template>
  <div data-testid="language-selector">
    <div>
      <TInputWithIcon
        v-model="searchQuery"
        data-testid="language-search-input"
        :placeholder="$t('layout.search_language')"
        icon-left
      >
        <template #icon>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.7812 15.6289C17.0547 15.9023 17.0547 16.3125 16.7812 16.5586C16.6719 16.6953 16.5078 16.75 16.3438 16.75C16.1523 16.75 15.9883 16.6953 15.8516 16.5586L12.1875 12.8945C11.2031 13.6875 9.97266 14.125 8.66016 14.125C5.54297 14.125 3 11.582 3 8.4375C3 5.32031 5.51562 2.75 8.66016 2.75C11.7773 2.75 14.3477 5.32031 14.3477 8.4375C14.3477 9.77734 13.9102 11.0078 13.1172 11.9648L16.7812 15.6289ZM4.3125 8.4375C4.3125 10.8711 6.25391 12.8125 8.6875 12.8125C11.0938 12.8125 13.0625 10.8711 13.0625 8.4375C13.0625 6.03125 11.0938 4.0625 8.6875 4.0625C6.25391 4.0625 4.3125 6.03125 4.3125 8.4375Z"
              fill="currentColor"
            />
          </svg>
        </template>
      </TInputWithIcon>
    </div>

    <div class="my-2">
      <ul class="m-0 list-none overflow-hidden rounded-md p-0">
        <VueCustomScrollbar
          ref="langScrollbar"
          style="position: relative; margin: auto; width: 322px"
          :style="{ height: calculatedHeightLangs + 'px' }"
          :settings="{ wheelPropagation: false }"
        >
          <li
            v-for="(language, i) in filteredLanguages"
            :key="language.code"
            :ref="
              (el) => {
                refLangs[i] = el;
              }
            "
            data-testid="language-item"
            class="cursor-pointer px-3 py-1 pt-2 font-inter !text-xs font-medium leading-4 transition duration-300"
            :class="{
              'bg-gray-400 !text-gray-50':
                preselectedLanguage === language.code,
              'bg-gray-600 !text-gray-100':
                preselectedLanguage !== language.code,
            }"
            @click="selectLanguage(language.code)"
            @mouseover="preselectByMoseover(language)"
          >
            <div class="language-info">
              <span>{{ language.name }} ({{ language.nativeName }})</span>
              <br />
              <span
                class="leading-2 text-[11px] text-gray-200 transition duration-300"
                :class="{
                  '!text-gray-100': preselectedLanguage === language.code,
                }"
                >{{ language.code }}</span
              >
            </div>
          </li>
        </VueCustomScrollbar>
      </ul>
    </div>
    <TButton class="w-full" @click="emit('cancel')">{{
      $t("editor.cancel")
    }}</TButton>
  </div>
</template>

<script setup lang="ts">
  import { IMultilingualLanguage } from "@/schemas/language/languageSchema";
  import { TInputWithIcon, TButton } from "@topol/twig-ui";
  import { PerfectScrollbar as VueCustomScrollbar } from "vue3-perfect-scrollbar";
  import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";

  const props = defineProps<{
    langsToUse: IMultilingualLanguage;
  }>();

  const emit = defineEmits(["select", "cancel"]);

  const searchQuery = ref("");
  const preselectedLanguage = ref<string | null>(null);
  const keyMove = ref(false);
  const langScrollbar = ref();
  const refLangs = ref<any[]>([]);

  const filteredLanguages = computed(() => {
    const preFiltered = props.langsToUse;

    if (!searchQuery.value) return preFiltered;

    const query = searchQuery.value.toLowerCase();
    const filtered = new Set<IMultilingualLanguage[0]>();

    preFiltered.map((lang) => {
      if (lang.code.startsWith(query)) {
        filtered.add(lang);
      }
    });

    preFiltered.map((lang) => {
      if (
        lang.name.toLowerCase().includes(query) ||
        lang.nativeName.toLowerCase().includes(query)
      ) {
        filtered.add(lang);
      }
    });

    return Array.from(filtered);
  });

  const calculatedHeightLangs = computed(() => {
    const height = filteredLanguages.value.length * 44;

    if (height > 325) {
      return 325;
    }

    return height;
  });

  function preselectByMoseover(language: IMultilingualLanguage[0]) {
    if (keyMove.value) return;
    preselectedLanguage.value = language.code;
  }

  const selectLanguage = (languageCode: string) => {
    searchQuery.value = "";
    emit("select", languageCode);
  };

  watch(filteredLanguages, () => {
    preselectedLanguage.value = filteredLanguages.value[0]?.code;
  });

  function keyEvents(event: KeyboardEvent) {
    if (!langScrollbar.value || !preselectedLanguage.value) return;
    keyMove.value = true;
    const index = filteredLanguages.value.findIndex(
      (item) => item.code === preselectedLanguage.value
    );
    if (event.key === "ArrowDown") {
      if (index < filteredLanguages.value.length - 1) {
        preselectedLanguage.value = filteredLanguages.value[index + 1].code;
        scrollIntoView(index + 1);
      }
    } else if (event.key === "ArrowUp") {
      if (index > 0) {
        preselectedLanguage.value = filteredLanguages.value[index - 1].code;
        scrollIntoView(index - 1);
      }
    } else if (event.key === "Enter") {
      selectLanguage(preselectedLanguage.value);
    }
    setTimeout(() => {
      keyMove.value = false;
    }, 50);
  }

  function scrollIntoView(index: number) {
    const itemBottom =
      refLangs.value[index].offsetTop + refLangs.value[index].offsetHeight;
    const containerBottom =
      langScrollbar.value.$el.scrollTop + langScrollbar.value.$el.clientHeight;
    const itemTop = refLangs.value[index].offsetTop;
    const containerTop = langScrollbar.value.$el.scrollTop;
    if (itemBottom > containerBottom) {
      langScrollbar.value.$el.scrollTop =
        itemBottom - langScrollbar.value.$el.clientHeight;
    }
    if (itemTop < containerTop) {
      langScrollbar.value.$el.scrollTop = itemTop;
    }
  }

  onMounted(() => {
    document.addEventListener("keydown", keyEvents);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", keyEvents);
  });
</script>
